import { useCallback, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import {
  MenuContext,
  MenuTriggerContext,
} from '@components/primitives/Menu/context';
import { MenuEmpty } from '@components/primitives/Menu/MenuEmpty';
import { MenuSeparator } from '@components/primitives/Menu/MenuSeparator';

import { MenuCheckbox } from './MenuCheckbox';
import { MenuHeading } from './MenuHeading';
import { MenuItem } from './MenuItem';
import { MenuLabel } from './MenuLabel';
import { MenuList } from './MenuList';
import { MenuRadioGroup } from './MenuRadioGroup';
import { MenuRadioItem } from './MenuRadioItem';
import { MenuSubMenu } from './MenuSubMenu';
import { MenuTrigger } from './MenuTrigger';

type MenuProps = {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
};

export function Menu({
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  modal,
}: MenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const isOpen = open ?? uncontrolledOpen;

  const isControlled = open !== undefined;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  return (
    <MenuContext.Provider value={[isOpen, setOpen]}>
      <MenuTriggerContext.Provider value={triggerRef}>
        <DropdownMenu.Root open={isOpen} onOpenChange={setOpen} modal={modal}>
          {children}
        </DropdownMenu.Root>
      </MenuTriggerContext.Provider>
    </MenuContext.Provider>
  );
}

Menu.Trigger = MenuTrigger;
Menu.List = MenuList;
Menu.Item = MenuItem;
Menu.Label = MenuLabel;
Menu.CheckboxItem = MenuCheckbox;
Menu.RadioGroup = MenuRadioGroup;
Menu.RadioItem = MenuRadioItem;
Menu.SubMenu = MenuSubMenu;
Menu.Heading = MenuHeading;
Menu.Empty = MenuEmpty;
Menu.Separator = MenuSeparator;

export type { MenuItemProps } from './MenuItem';
